import { useEffect } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ApiError } from '../../api/problem'
import { PrintSheet } from '../../domains/characters/print/PrintSheet'
import { usePrintableSheet } from '../../domains/characters/print/usePrintableSheet'
import styles from '../../domains/characters/print/print.module.css'

/**
 * The printable character sheet — A4, ink on paper, one route.
 *
 * **It renders outside `AppShell` on purpose.** The shell is a sticky header, the primary
 * navigation and the assistant dock; none of them belong on a sheet of paper, and hiding them
 * with `@media print` would still mount them, still fetch for them, and still leave the print
 * preview showing an app around the page. A print view is a different document, not the same
 * document with things hidden.
 *
 * There is no PDF library here and no server round trip: the browser's own print pipeline
 * turns this into A4, and "Save as PDF" in the print dialog is the export. That is the whole
 * feature — see `print.module.css` for the page geometry.
 */
export function CharacterPrintPage() {
  const params = useParams<{ id: string }>()
  const characterId = Number(params.id)
  const printable = usePrintableSheet(characterId)

  // In the URL rather than in component state: a blank sheet is a thing you send someone,
  // bookmark, or come back to after fiddling with the print dialog, and all three want the
  // choice to survive.
  const [search, setSearch] = useSearchParams()
  const blank = search.get('blank') === '1'

  const setBlank = (next: boolean) => {
    const updated = new URLSearchParams(search)
    if (next) updated.set('blank', '1')
    else updated.delete('blank')
    setSearch(updated, { replace: true })
  }

  // The app's body carries a dark gradient (design/global.css). It would show around the
  // sheets in the preview and, with background graphics enabled, behind them on paper. The
  // class is removed on unmount, so navigating back restores the app's own surface.
  useEffect(() => {
    document.body.classList.add('printing')
    return () => document.body.classList.remove('printing')
  }, [])

  if (Number.isNaN(characterId)) {
    return <PrintMessage message="That character id is not a number." />
  }

  if (printable.isPending) {
    return <PrintMessage message="Preparing the sheet…" />
  }

  if (printable.isError || printable.sheet === undefined) {
    const notFound = printable.error instanceof ApiError && printable.error.kind === 'not-found'
    return (
      <PrintMessage
        message={
          notFound
            ? 'No such character, or it is not visible to you.'
            : 'Could not load this character.'
        }
        characterId={characterId}
      />
    )
  }

  const name = printable.sheet.sections.core?.name ?? 'Character'

  return (
    <div className={styles.viewport}>
      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>{name} — printable sheet</span>
        <label className={styles.toolbarOption}>
          <input
            type="checkbox"
            checked={blank}
            onChange={(event) => setBlank(event.target.checked)}
          />
          Blank sheet
        </label>
        <Link to={`/characters/${characterId}`}>Back to the sheet</Link>
        <button type="button" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <p className={styles.hint}>
        Choose A4 and “Save as PDF” in the print dialog to export, and untick “Headers and footers”
        to keep the browser’s date and URL off the page. Background graphics may be left off — the
        sheet is drawn in lines and prints the same either way.
        {blank && ' Blank sheet: every box and circle, none of the writing.'}
      </p>

      <PrintSheet sheet={printable.sheet} otherShapes={printable.otherShapes} blank={blank} />
    </div>
  )
}

function PrintMessage({ message, characterId }: { message: string; characterId?: number }) {
  return (
    <div className={styles.status}>
      <div>
        <p>{message}</p>
        <p>
          <Link to={characterId === undefined ? '/characters' : `/characters/${characterId}`}>
            Back
          </Link>
        </p>
      </div>
    </div>
  )
}
